/**
 * The Filters button, the modal behind it, and the line that says a filter is on.
 *
 * Three pieces because they belong in three places: the button sits in the app
 * header beside the range selector, the modal is a state you enter and leave,
 * and the "showing 12 of 340" line has to sit directly above the table it
 * describes. A reader who scrolls past the header must still be told that the
 * numbers under their eyes are a subset -- a filtered table that looks
 * unfiltered is the way to read a wrong total off this dashboard.
 *
 * The modal is deliberately not DrillInDialog: a drill-in is a wide read-only
 * table, this is a narrow form with a footer that commits. Nothing is applied
 * until Apply, so a half-built filter never briefly renumbers the page behind
 * the modal.
 *
 * Every filter renders the same way -- a searchable checkbox list -- which is
 * what makes the next filter one line in a `FilterDef[]` rather than a new
 * control. Multi-select is a superset of the single-select a month filter was
 * first asked for: picking exactly one month is "only show me Aug 2026".
 */
import { Filter, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  activeFilterCount,
  emptyFilterValues,
  type FilterDef,
  type FilterOption,
  type FilterValues,
  summariseFilters,
} from "@/components/profitability/filters";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

/** Above this many options the list gets a search box rather than a long scroll. */
const SEARCHABLE_FROM = 8;

/**
 * The header control. Carries the count because "is a filter on" has to be
 * answerable at a glance from the top of the page, without opening the modal.
 */
export function FilterButton({
  values,
  onOpen,
  disabled,
}: {
  values: FilterValues;
  onOpen: () => void;
  disabled?: boolean;
}) {
  const count = activeFilterCount(values);
  return (
    <Button
      type="button"
      variant={count > 0 ? "default" : "outline"}
      size="sm"
      onClick={onOpen}
      disabled={disabled}
      aria-label={count > 0 ? `Filters, ${count} active` : "Filters"}
      title={
        count > 0
          ? "Filters are on -- the tables below show a subset."
          : "Filter the table below by month or person."
      }
    >
      <Filter />
      Filters
      {count > 0 ? (
        <Badge variant="secondary" className="ml-1 h-5 min-w-5 justify-center px-1 tabular-nums">
          {count}
        </Badge>
      ) : null}
    </Button>
  );
}

function FilterSection<Row>({
  def,
  options,
  selected,
  onToggle,
  onSetAll,
}: {
  def: FilterDef<Row>;
  options: FilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onSetAll: (values: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const chosen = new Set(selected);
  const searchable = options.length > SEARCHABLE_FROM;
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) => option.value.toLowerCase().includes(needle));
  }, [options, query]);

  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium">
          {def.label}
          {selected.length > 0 ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground tabular-nums">
              {selected.length} of {options.length}
            </span>
          ) : null}
        </h3>
        {/* Named "None", not "Clear": within one filter, nothing selected means
            every option is included, so "Clear" would read as excluding all. */}
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground hover:underline"
            onClick={() => onSetAll(options.map((option) => option.value))}
          >
            Select all
          </button>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground hover:underline"
            onClick={() => onSetAll([])}
          >
            None
          </button>
        </div>
      </div>
      {def.help ? <p className="text-xs text-muted-foreground">{def.help}</p> : null}
      {searchable ? (
        <Input
          className="h-8"
          value={query}
          placeholder={`Search ${def.label.toLowerCase()}`}
          onChange={(event) => setQuery(event.target.value)}
        />
      ) : null}
      {options.length === 0 ? (
        // Options come from the rows on the page, so an empty list is a real
        // statement about the data rather than a loading state.
        <p className="text-sm text-muted-foreground">
          Nothing to filter by &mdash; no rows in the selected range carry a{" "}
          {def.label.toLowerCase()}.
        </p>
      ) : (
        <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-2">
          {shown.length === 0 ? (
            <p className="px-1 py-2 text-sm text-muted-foreground">
              No {def.label.toLowerCase()} matches &ldquo;{query}&rdquo;.
            </p>
          ) : (
            shown.map((option) => {
              const id = `filter-${def.key}-${option.value}`;
              return (
                <label
                  key={option.value}
                  htmlFor={id}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted/60"
                >
                  <Checkbox
                    id={id}
                    checked={chosen.has(option.value)}
                    onCheckedChange={() => onToggle(option.value)}
                  />
                  <span className="min-w-0 flex-1 truncate">{option.value}</span>
                  {option.count === undefined ? null : (
                    <span
                      className="shrink-0 text-xs text-muted-foreground tabular-nums"
                      title={`${option.count} row${option.count === 1 ? "" : "s"} on this page`}
                    >
                      {option.count}
                    </span>
                  )}
                </label>
              );
            })
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The modal. Edits a draft and commits it on Apply, so the page behind it does
 * not renumber while the reader is still choosing.
 */
export function FilterDialog<Row>({
  open,
  onOpenChange,
  defs,
  options,
  values,
  onApply,
  /** What the filters actually narrow, said out loud -- see the page header. */
  scopeNote,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defs: readonly FilterDef<Row>[];
  options: Record<string, FilterOption[]>;
  values: FilterValues;
  onApply: (next: FilterValues) => void;
  scopeNote: string;
}) {
  const [draft, setDraft] = useState<FilterValues>(values);

  // Reseed on open, not on every change to `values`: the draft is meant to
  // diverge from what is applied for as long as the modal is up.
  useEffect(() => {
    if (open) setDraft(values);
  }, [open, values]);

  const draftCount = activeFilterCount(draft);

  const toggle = (key: string, value: string) => {
    setDraft((prev) => {
      const current = prev[key] ?? [];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value],
      };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] w-[520px] max-w-[92vw] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Filters</DialogTitle>
          <DialogDescription>{scopeNote}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {defs.map((def) => (
            <FilterSection
              key={def.key}
              def={def}
              options={options[def.key] ?? []}
              selected={draft[def.key] ?? []}
              onToggle={(value) => toggle(def.key, value)}
              onSetAll={(next) => setDraft((prev) => ({ ...prev, [def.key]: next }))}
            />
          ))}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={draftCount === 0}
            onClick={() => setDraft(emptyFilterValues(defs))}
          >
            Clear all
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                onApply(draft);
                onOpenChange(false);
              }}
            >
              Apply
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The line above a filtered table: which filters are on, how many rows survived,
 * and a one-click way off.
 *
 * Renders nothing when nothing is filtered, so an unfiltered page is not
 * carrying a permanent empty banner.
 */
export function ActiveFilterBar<Row>({
  defs,
  values,
  shown,
  total,
  countLabel,
  onClear,
  onEdit,
  scopeNote,
}: {
  defs: readonly FilterDef<Row>[];
  values: FilterValues;
  /** Rows after filtering. */
  shown: number;
  /** Rows before filtering. */
  total: number;
  /**
   * Replaces the "X of Y rows" ratio when the SERVER applied the filter.
   * There is no ratio to report in that case: everything returned matched.
   */
  countLabel?: string;
  onClear: () => void;
  onEdit: () => void;
  /**
   * What is filtered and what is not, in one sentence.
   *
   * A prop rather than the fixed string it used to be, because the two screens
   * that show this bar leave DIFFERENT things unfiltered. On the project page
   * the filter narrows the tables while the header and the other tabs keep
   * reporting the whole range. On a paged list it narrows the page in front of
   * the reader and not the pages behind it -- a materially different caveat,
   * and the wrong one is worse than none.
   */
  scopeNote?: string;
}) {
  const parts = summariseFilters(defs, values);
  if (parts.length === 0) return null;

  return (
    <div
      data-testid="active-filters"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
    >
      <Filter className="size-4 shrink-0 text-primary" />
      <span className="font-medium">Filtered</span>
      {parts.map((part) => (
        <Badge key={part} variant="secondary" className="font-normal">
          {part}
        </Badge>
      ))}
      <span className="text-muted-foreground tabular-nums">
        {/* "X of Y rows" is a CLIENT-side filter's sentence -- what survived,
            against what was in memory. When the SERVER applied the filter
            there is no such ratio: every row that came back matched, and the
            rows that did not were never sent. countLabel carries the honest
            line for that case. */}
        {countLabel ?? `${shown.toLocaleString()} of ${total.toLocaleString()} rows`}
      </span>
      {/* The scope, because something on every screen that shows this bar is
          NOT filtered -- the project page's header and other tabs, or the pages
          of a paged list either side of this one. Left unsaid, a filtered table
          sitting under an unfiltered figure reads as one number and its
          breakdown. */}
      <span className="text-xs text-muted-foreground">
        {scopeNote ?? "These tables only — the project header and the other tabs are unfiltered."}
      </span>
      <div className="ml-auto flex shrink-0 items-center gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClear}>
          <X />
          Clear
        </Button>
      </div>
    </div>
  );
}
